import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import './Judgment.css';

const JudgmentOrder = () => {
  const { judgments, setJudgments, cases, setCases, addAuditLog } = useApp();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    caseId: '',
    judgmentDate: new Date().toISOString().split('T')[0],
    judgmentType: 'Judgment',
    summary: '',
    status: 'Final',
  });
  const [editingId, setEditingId] = useState(null);

  const judgmentTypes = ['Judgment', 'Order', 'Decree', 'Interim Order'];

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const caseItem = cases.find(c => c.id === formData.caseId);
    
    if (editingId) {
      setJudgments(judgments.map(j => 
        j.id === editingId 
          ? { 
              ...j, 
              ...formData,
              caseNumber: caseItem?.caseNumber || j.caseNumber,
              caseTitle: caseItem?.caseTitle || j.caseTitle,
            }
          : j
      ));
      addAuditLog('Judgment Updated', `Judgment updated for case ${caseItem?.caseNumber}`);
    } else {
      const newJudgment = {
        id: Date.now().toString(),
        ...formData,
        caseNumber: caseItem?.caseNumber || '',
        caseTitle: caseItem?.caseTitle || '',
        orderUrl: `#judgment-${Date.now()}`, // Mock PDF URL
        createdAt: new Date().toISOString(),
      };
      setJudgments([...judgments, newJudgment]);
      
      // Update case status to Disposed
      if (caseItem) {
        setCases(cases.map(c => 
          c.id === caseItem.id
            ? { ...c, status: 'Disposed' }
            : c
        ));
      }
      
      addAuditLog('Judgment Entered', `Judgment entered for case ${caseItem?.caseNumber}`);
    }
    
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      caseId: '',
      judgmentDate: new Date().toISOString().split('T')[0],
      judgmentType: 'Judgment',
      summary: '',
      status: 'Final',
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (judgment) => {
    setFormData(judgment);
    setEditingId(judgment.id);
    setShowForm(true);
  };

  const handleDelete = (id) => {
    const judgment = judgments.find(j => j.id === id);
    setJudgments(judgments.filter(j => j.id !== id));
    addAuditLog('Judgment Deleted', `Judgment deleted for case ${judgment.caseNumber}`);
  };

  return (
    <div className="judgment-container">
      <div className="judgment-header">
        <h1>⚖️ Judgment & Order</h1>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary">
          {showForm ? 'Cancel' : '+ Enter Judgment'}
        </button>
      </div>

      {showForm && (
        <div className="judgment-form-card">
          <h2>{editingId ? 'Edit Judgment' : 'Enter New Judgment'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label>Case *</label>
                <select
                  value={formData.caseId}
                  onChange={(e) => setFormData({...formData, caseId: e.target.value})}
                  required
                >
                  <option value="">Select Case</option>
                  {cases.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.caseNumber} - {c.caseTitle}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Judgment Date *</label>
                <input
                  type="date"
                  value={formData.judgmentDate}
                  onChange={(e) => setFormData({...formData, judgmentDate: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Judgment Type *</label>
                <select
                  value={formData.judgmentType}
                  onChange={(e) => setFormData({...formData, judgmentType: e.target.value})}
                  required
                >
                  {judgmentTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Status *</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  required
                >
                  <option value="Final">Final</option>
                  <option value="Interim">Interim</option>
                  <option value="Draft">Draft</option>
                </select>
              </div>

              <div className="form-group full-width">
                <label>Summary *</label>
                <textarea
                  value={formData.summary}
                  onChange={(e) => setFormData({...formData, summary: e.target.value})}
                  rows="6"
                  placeholder="Enter judgment summary"
                  required
                />
              </div>

              <div className="form-group full-width">
                <label>Order Upload (Mock)</label>
                <input
                  type="file"
                  accept=".pdf"
                  disabled
                  className="file-input"
                />
                <small className="file-note">Note: PDF upload is mocked for this demo. In production, this would handle actual PDF uploads.</small>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingId ? 'Update' : 'Enter'} Judgment
              </button>
              <button type="button" onClick={resetForm} className="btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="judgment-table-card">
        <h2>Judgments & Orders ({judgments.length})</h2>
        {judgments.length > 0 ? (
          <div className="table-container">
            <table className="judgment-table">
              <thead>
                <tr>
                  <th>Case Number</th>
                  <th>Case Title</th>
                  <th>Judgment Date</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Summary</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {judgments.sort((a, b) => new Date(b.judgmentDate) - new Date(a.judgmentDate)).map(judgment => (
                  <tr key={judgment.id}>
                    <td><strong>{judgment.caseNumber}</strong></td>
                    <td>{judgment.caseTitle}</td>
                    <td>{new Date(judgment.judgmentDate).toLocaleDateString()}</td>
                    <td><span className="judgment-type-badge">{judgment.judgmentType}</span></td>
                    <td>
                      <span className={`status-badge status-${judgment.status.toLowerCase()}`}>
                        {judgment.status}
                      </span>
                    </td>
                    <td className="summary-cell">{judgment.summary.substring(0, 100)}...</td>
                    <td>
                      <button className="btn-view">View</button>
                      <button className="btn-download">Download PDF</button>
                      <button onClick={() => handleEdit(judgment)} className="btn-edit">Edit</button>
                      <button onClick={() => handleDelete(judgment.id)} className="btn-delete">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="empty-state">No judgments entered yet. Click "Enter Judgment" to get started.</p>
        )}
      </div>
    </div>
  );
};

export default JudgmentOrder;

